import { formatCount, formatPercent, rateToInput } from '@/lib/format';
import type { DisplayNodes, FunnelResult } from '@/model/types';
import { useFunnelStore } from '@/store/useFunnelStore';
import { NumberField } from './NumberField';
import { DerivedNote, Note, Section } from './parts';

interface Props {
  result: FunnelResult;
  display: DisplayNodes;
}

export function ChaseStage({ result, display }: Props) {
  const add = useFunnelStore((s) => s.addCohort);
  const update = useFunnelStore((s) => s.updateCohort);
  const remove = useFunnelStore((s) => s.removeCohort);

  return (
    <>
      <Section title="Follow-up cohorts">
        <div className="crud-list">
          {result.cohorts.map((cohort, i) => {
            const shown = display.cohorts[i];
            return (
              <div key={cohort.id} className="crud-card">
                <div className="crud-head">
                  <input
                    className="crud-name"
                    type="text"
                    value={cohort.name}
                    aria-label="Cohort name"
                    onChange={(event) => update(cohort.id, { name: event.target.value })}
                  />
                  <button
                    type="button"
                    className="crud-delete"
                    aria-label={`Remove ${cohort.name}`}
                    onClick={() => remove(cohort.id)}
                  >
                    ×
                  </button>
                </div>

                <div className="crud-fields is-three">
                  <NumberField
                    label="Share"
                    value={rateToInput(cohort.share)}
                    max={100}
                    step={0.5}
                    suffix="%"
                    onCommit={(pct) => update(cohort.id, { share: pct / 100 })}
                  />
                  <NumberField
                    label="Reach"
                    value={rateToInput(cohort.reach)}
                    max={100}
                    step={0.5}
                    suffix="%"
                    onCommit={(pct) => update(cohort.id, { reach: pct / 100 })}
                  />
                  <NumberField
                    label="Complete"
                    value={rateToInput(cohort.completion)}
                    max={100}
                    step={0.5}
                    suffix="%"
                    onCommit={(pct) => update(cohort.id, { completion: pct / 100 })}
                  />
                </div>

                <p className="crud-derived">
                  {formatCount(shown?.pool ?? 0)} pooled →{' '}
                  <strong>{formatCount(shown?.reached ?? 0)}</strong> reached →{' '}
                  <strong>{formatCount(shown?.completed ?? 0)}</strong> completed
                </p>
              </div>
            );
          })}
        </div>

        <button type="button" className="crud-add" onClick={add}>
          + Add cohort
        </button>
      </Section>

      <DerivedNote
        rows={[
          ['Blended reach', formatPercent(result.rates.followupReach)],
          ['Blended completion', formatPercent(result.rates.followupCompletion)],
        ]}
      />

      <Note tone="warn">
        Completion is not numerator status. Structured, LOINC-coded capture and the ≤30-day window
        are separate gates after column 6, not drawn on this canvas.
      </Note>
    </>
  );
}
